import { makeAutoObservable } from "mobx";

import { faHome, IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import { AppRole, UserPermissionsDto } from "../../@api";

import { enabledFeatures } from "../features";
import { translationKeys } from "../models/app-translation-keys";
import { RouteConstants } from "../models/enums/route-constants.enum";
import { IMenuItem } from "../models/interfaces/menu-item.interface";

export enum ExternalLinkKeys {
  Contact = "contact",
  PrivacyPolicity = "privacyPolicy",
  Impressum = "impressum",
}

interface IExternalLink {
  key: ExternalLinkKeys;
  labelKey: string;
  urlKey?: string;
  url?: string;
  icon?: IconDefinition;
}

export class NavigationStore {
  public landingPage: IMenuItem = {
    label: translationKeys.pages?.landingPage.title ?? "",
    icon: faHome,
    url: "/",
    path: RouteConstants.LandingPage,
    showFooter: true,
    hasSideBar: false,
  };

  public pageNotFound: IMenuItem = {
    label: translationKeys.pages?.pageNotFound.title ?? "",
    url: RouteConstants.PageNotFound,
    path: RouteConstants.PageNotFound,
    showFooter: true,
    hasSideBar: false,
  };

  private readonly _features = enabledFeatures();

  private _externalNavigationLinks: IExternalLink[] = [
    {
      key: ExternalLinkKeys.Contact,
      labelKey: translationKeys.footer.contact,
      urlKey: translationKeys.footer.contactUrl,
    },
    {
      key: ExternalLinkKeys.PrivacyPolicity,
      labelKey: translationKeys.footer.privacyPolicy,
      urlKey: translationKeys.footer.privacyPolicyUrl,
    },
    {
      key: ExternalLinkKeys.Impressum,
      labelKey: translationKeys.footer.impressum,
      urlKey: translationKeys.footer.impressumUrl,
    },
  ];
  private _currentActiveMenuItem: IMenuItem | undefined;

  constructor() {
    makeAutoObservable(this);
  }

  public set externalNavigationLinks(links: IExternalLink[]) {
    this._externalNavigationLinks = links.slice();
  }

  public setExternalNavigationLinkURL = (key: ExternalLinkKeys, url: string) => {
    this._externalNavigationLinks = this._externalNavigationLinks
      ?.map(link => {
        if (link.key === key) {
          return { ...link, url };
        }
        return link;
      })
      .slice();
  };

  public get externalNavigationLinks(): IExternalLink[] | undefined {
    return this._externalNavigationLinks;
  }

  public setActiveMenuItem = (menuItem: IMenuItem | undefined) => {
    this._currentActiveMenuItem = menuItem;
  };

  public getPermissionedFeatureMenuItems(permissions: AppRole[]): IMenuItem[] {
    return this._features.filter(feature => permissions.includes(feature.role)).map(feature => feature.menuItem);
  }

  public setActiveMenuItemBasedOnPath = (path: string | undefined, permissions: UserPermissionsDto) => {
    const activeMenuItem = this.getPermissionedFeatureMenuItems(permissions.roles)
      .filter(item => !!item.path?.length)
      .find(item => path?.replace("/", "").includes(item.path ?? ""));

    this.setActiveMenuItem(activeMenuItem ?? this.landingPage);
  };

  public get currentActiveMenuItem(): IMenuItem | undefined {
    return this._currentActiveMenuItem;
  }
}
